import { useState, useEffect } from "react";

import { json, redirect } from "@remix-run/node";
import type { ActionFunction, LoaderFunction } from "@remix-run/node";
import {
    Form,
    Link,
    useActionData,
    useLocation,
    useNavigation,
} from "@remix-run/react";
import type { User as SupabaseUser } from "@supabase/supabase-js";
import { FileText, Glasses, Lock, LogOut, User, Users } from "lucide-react";

import Layout from "~/components/layout";
import Logo from "~/components/logo";
import { Button } from "~/components/ui/button";
import { P } from "~/components/ui/typography";
import { useToast } from "~/components/ui/use-toast";
import { withCors } from "~/config/cors";
import { useSupabaseSession } from "~/context/SupabaseSessionContext";
import { useAnonymous } from "~/hooks/use-anonymous";
import { getUnauditableSystemAdminSupabaseClient } from "~/integrations/supabase/client.server";
import { createSupabaseServerClient } from "~/integrations/supabase/ssr.server";
import { trackEvent } from "~/lib/analytics";
import { PRODUCT_NAME } from "~/lib/env.shared";
import { handleLogout } from "~/lib/logout";
import { useIsPractitioner } from "~/lib/user";
import { getLogger } from "~/modules.server/logger";
import { profileRepository } from "~/modules.server/profile/repository";
import { pageMeta } from "~/utils/meta";
import { SUPPORT_EMAIL } from "~/utils.server/env";

import { CookieBanner } from "../components/cookie-banner";

const Landing = () => {
    const { toast } = useToast();
    const location = useLocation();
    const navigation = useNavigation();
    const actionData = useActionData<{ error?: string }>();
    const { isAnonymous } = useAnonymous();
    const [hasTrackedAnonymous, setHasTrackedAnonymous] = useState(false);
    const [isProcessingLogout, setIsProcessingLogout] = useState(false);
    const { isPractitioner, isLoading: isPractitionerLoading } =
        useIsPractitioner();

    // AIDEV-NOTE: Use session from context instead of local state - ensures sync with server
    const { userId, loading: isLoadingSession } = useSupabaseSession();
    const isAuthenticated = !!userId;
    const isLoading = isLoadingSession;
    const isSubmittingAnonymous =
        navigation.state === "submitting" &&
        navigation.formData?.get("intent") === "anonymous-login";
    const handleLandingLogout = async () => {
        if (isProcessingLogout) return;

        setIsProcessingLogout(true);
        try {
            await handleLogout();
        } finally {
            // Location change interrupts state update, but keep for tests / fallback
            setIsProcessingLogout(false);
        }
    };

    // Handle action errors
    useEffect(() => {
        if (actionData?.error) {
            toast({
                title: "Error",
                description: actionData.error,
                variant: "destructive",
            });
        }
    }, [actionData, toast]);

    // Track anonymous signup after successful authentication
    useEffect(() => {
        if (isAuthenticated && isAnonymous && !hasTrackedAnonymous) {
            trackEvent("anonymous signup", {});
            setHasTrackedAnonymous(true);
        }
    }, [isAuthenticated, isAnonymous, hasTrackedAnonymous]);

    if (isLoading) {
        return <Layout narrow={true} loading={true} hideLogo={true} />;
    }

    return (
        <Layout
            hideBack={true}
            offsetTop={true}
            narrow={true}
            hideLogo={true}
            loading={isLoading || isPractitionerLoading}
        >
            <div className="space-y-8 text-center">
                <Logo size="lg" />
                <p className="text-center">
                    Clarity, continuity, and connection in your IFS journey.
                </p>
                <div className="flex flex-col items-stretch space-y-4">
                    {isAuthenticated ? (
                        <>
                            {isPractitioner && (
                                <Link to="/practitioner">
                                    <Button
                                        className="text-md flex h-12 w-full px-8"
                                        variant="subtle"
                                    >
                                        <Users className="mr-2 size-5" />
                                        My Clients
                                    </Button>
                                </Link>
                            )}

                            <Link to="/sessions">
                                <Button
                                    className="text-md mt-4 flex h-12 w-full px-8"
                                    variant="subtle"
                                >
                                    <FileText className="mr-2 size-5" />
                                    My Sessions
                                </Button>
                            </Link>

                            {isAnonymous && (
                                <Link to="/link-account">
                                    <Button
                                        className="text-md flex h-12 w-full px-8"
                                        variant="subtle"
                                    >
                                        <User className="mr-2 size-5" />
                                        Create an account
                                    </Button>
                                </Link>
                            )}

                            {isAnonymous && (
                                <Link to={"/login" + location.search}>
                                    <Button
                                        className="text-md flex h-12 w-full px-8"
                                        variant="ghost"
                                    >
                                        <User className="mr-2 size-5" />
                                        Log In
                                    </Button>
                                </Link>
                            )}

                            {!isAnonymous && (
                                <Button
                                    type="button"
                                    onClick={handleLandingLogout}
                                    className="text-md flex h-12 w-full px-8"
                                    variant={
                                        isAnonymous && import.meta.env.DEV
                                            ? "internal"
                                            : "ghost"
                                    }
                                    isLoading={isProcessingLogout}
                                >
                                    <LogOut className="mr-2 size-5" />
                                    Log Out
                                </Button>
                            )}
                        </>
                    ) : (
                        <>
                            <Form method="post">
                                <input
                                    type="hidden"
                                    name="intent"
                                    value="anonymous-login"
                                />
                                <input
                                    type="hidden"
                                    name="redirectTo"
                                    value={
                                        "/onboarding/terms" + location.search
                                    }
                                />
                                <Button
                                    type="submit"
                                    className="text-md h-12 w-full px-8"
                                    variant="subtle"
                                    isLoading={isSubmittingAnonymous}
                                >
                                    <Glasses className="mr-2 size-5" />
                                    Try anonymously
                                </Button>
                            </Form>

                            <Link to={"/signup" + location.search}>
                                <Button
                                    className="text-md h-12 w-full px-8"
                                    variant="subtle"
                                >
                                    <User className="mr-2 size-5" />
                                    Create an account
                                </Button>
                            </Link>
                            <Link to={"/login" + location.search}>
                                <Button
                                    className="text-md h-12 w-full px-8"
                                    variant="ghost"
                                >
                                    <Lock className="mr-2 size-5" />
                                    Log into existing account
                                </Button>
                            </Link>
                        </>
                    )}
                </div>

                <footer className="space-y-2 text-xs font-medium text-rose-900">
                    <div className="mb-10 flex justify-center gap-1">
                        <Link to="/privacy">
                            <button type="button" className="text-rose-500">
                                Privacy Policy
                            </button>
                        </Link>
                        <span> &bull; </span>
                        <Link to="/terms">
                            <button type="button" className="text-rose-500">
                                Terms of Use
                            </button>
                        </Link>
                        <span> &bull; </span>
                        <Link to={`mailto:${SUPPORT_EMAIL}`}>
                            <button type="button" className="text-rose-500">
                                Support
                            </button>
                        </Link>
                    </div>
                    <P className="text-center text-xs">
                        &copy;2025 Evolve Labs Inc.
                    </P>
                </footer>
            </div>
        </Layout>
    );
};

export const meta = pageMeta(
    PRODUCT_NAME,
    "Clarity, continuity, and connection in your IFS journey.",
);

export const action: ActionFunction = withCors(async ({ request }) => {
    const formData = await request.formData();
    const intent = formData.get("intent");

    if (intent === "anonymous-login") {
        const redirectTo = formData.get("redirectTo") || "/onboarding/terms";

        const { headers, supabase: serverClient } =
            createSupabaseServerClient(request);

        const { data, error } = await serverClient.auth.signInAnonymously();

        if (error) {
            return json(
                {
                    error: "Failed to create anonymous session. Please try again.",
                },
                { status: 400 },
            );
        }

        if (data?.user) {
            await ensureAnonymousProfile(data.user);
        }

        // AIDEV-NOTE: Analytics tracking handled client-side after successful redirect
        return redirect(redirectTo.toString(), { headers });
    }

    if (intent === "logout") {
        const { headers, supabase: serverClient } =
            createSupabaseServerClient(request);

        await serverClient.auth.signOut();

        // AIDEV-NOTE: Clear auth cookies and redirect - matches api.auth.logout behavior
        return redirect("/landing", { headers });
    }

    return json({ error: "Invalid intent" }, { status: 400 });
});

export const loader: LoaderFunction = withCors(async ({ request }) => json({}));

export default function LandingRoute() {
    return <Landing />;
}

async function ensureAnonymousProfile(user: SupabaseUser) {
    if (!user?.id) {
        return;
    }

    const adminClient = getUnauditableSystemAdminSupabaseClient();

    try {
        const existingProfile = await profileRepository.getProfileById(
            user.id,
            adminClient,
        );

        if (existingProfile) {
            return;
        }

        // AIDEV-NOTE: Create barebones profile for anonymous users so protected routes don't bounce back to /landing
        await profileRepository.createProfile(
            {
                id: user.id,
                authUserId: user.id,
                email: user.email ?? null,
                onboarding_status: "not_started",
            },
            adminClient,
        );
    } catch (error) {
        const logger = getLogger().withMetadata({ anonymousUserId: user.id });
        logger
            .withError(error as Error)
            .error("Failed to ensure anonymous profile after signup");
        throw error;
    }
}
